import LegalPageLayout from '../../components/feature/LegalPageLayout';

const sections = [
  { id: 'introduction', title: 'Introduction' },
  { id: 'what-are-cookies', title: 'What Are Cookies?' },
  { id: 'types-of-cookies', title: 'Types of Cookies We Use' },
  { id: 'essential-cookies', title: 'Essential Cookies' },
  { id: 'analytics-cookies', title: 'Analytics Cookies' },
  { id: 'functionality-cookies', title: 'Functionality Cookies' },
  { id: 'third-party-cookies', title: 'Third-Party Cookies' },
  { id: 'cookie-duration', title: 'Cookie Duration' },
  { id: 'managing-cookies', title: 'Managing Cookies' },
  { id: 'consent', title: 'Your Consent' },
  { id: 'changes', title: 'Changes to This Policy' },
  { id: 'contact', title: 'Contact Us' },
];

export default function CookiePolicyPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://toolnova.ai';
  return (
    <LegalPageLayout
      title="Cookie Policy"
      lastUpdated="January 2025"
      sections={sections}
      seo={{
        title: 'Cookie Policy - How ToolNova Uses Cookies | ToolNova',
        description: 'Learn how ToolNova uses cookies and tracking technologies to improve your experience on our AI tools discovery platform.',
        keywords: 'toolnova cookie policy, cookies, tracking, browser cookies, analytics',
        canonical: `${siteUrl}/cookie-policy`,
        schema: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Cookie Policy',
          description: 'ToolNova Cookie Policy — how we use cookies and similar technologies on our website.',
          url: `${siteUrl}/cookie-policy`,
          publisher: {
            '@type': 'Organization',
            name: 'ToolNova',
            url: siteUrl,
          },
        },
      }}
    >
      <section id="introduction">
        <h2>Introduction</h2>
        <p>
          This Cookie Policy explains how Toolnova uses cookies and similar tracking technologies when you visit our website. We use cookies to improve your browsing experience, analyze website traffic, and understand how visitors interact with our AI tools directory.
        </p>
        <p>
          By using Toolnova, you consent to the use of cookies in accordance with this Cookie Policy. If you do not agree to our use of cookies, you should adjust your browser settings accordingly or refrain from using our website.
        </p>
      </section>

      <section id="what-are-cookies">
        <h2>What Are Cookies?</h2>
        <p>
          Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. Cookies are widely used to make websites work more efficiently and provide information to website owners.
        </p>
        
        <h3>How Cookies Work</h3>
        <p>
          When you visit Toolnova, our server sends a cookie to your device. Your browser stores the cookie and sends it back to our server each time you visit a new page on our website. This allows us to recognize your device and remember your preferences.
        </p>

        <h3>Similar Technologies</h3>
        <p>
          In addition to cookies, we may use similar technologies such as:
        </p>
        <ul>
          <li><strong>Web Beacons:</strong> Small graphic images (also known as pixel tags) that track user behavior</li>
          <li><strong>Local Storage:</strong> Technology that stores data locally in your browser</li>
          <li><strong>Session Storage:</strong> Temporary storage that is cleared when you close your browser</li>
        </ul>
        <p>
          For simplicity, we refer to all these technologies as "cookies" in this policy.
        </p>
      </section>

      <section id="types-of-cookies">
        <h2>Types of Cookies We Use</h2>
        <p>
          Toolnova uses different types of cookies for various purposes. Here's an overview of the categories of cookies we use:
        </p>
        
        <h3>First-Party vs. Third-Party Cookies</h3>
        <ul>
          <li><strong>First-Party Cookies:</strong> Set by Toolnova directly and used only by our website</li>
          <li><strong>Third-Party Cookies:</strong> Set by external services we use (such as analytics providers) and may be used across multiple websites</li>
        </ul>

        <h3>Session vs. Persistent Cookies</h3>
        <ul>
          <li><strong>Session Cookies:</strong> Temporary cookies that are deleted when you close your browser</li>
          <li><strong>Persistent Cookies:</strong> Remain on your device for a set period or until you delete them</li>
        </ul>
      </section>

      <section id="essential-cookies">
        <h2>Essential Cookies</h2>
        <p>
          Essential cookies are necessary for the basic functionality of our website. These cookies enable core features such as page navigation, access to secure areas, and form submissions.
        </p>
        
        <h3>What Essential Cookies Do</h3>
        <ul>
          <li>Remember your cookie consent preferences</li>
          <li>Maintain your session as you navigate between pages</li>
          <li>Enable secure form submissions</li>
          <li>Prevent security risks and fraudulent activity</li>
        </ul>

        <h3>Why They're Necessary</h3>
        <p>
          Without essential cookies, Toolnova cannot function properly. These cookies do not collect personal information and cannot be disabled through our cookie settings. However, you can block them through your browser settings, though this may affect your ability to use certain features of our website.
        </p>
      </section>

      <section id="analytics-cookies">
        <h2>Analytics Cookies</h2>
        <p>
          Analytics cookies help us understand how visitors interact with Toolnova by collecting and reporting information anonymously. This data helps us improve our website and provide a better user experience.
        </p>
        
        <h3>What We Track</h3>
        <ul>
          <li>Number of visitors and page views</li>
          <li>How visitors navigate through our website</li>
          <li>Which pages are most popular</li>
          <li>How long visitors spend on each page</li>
          <li>Which AI tools are most frequently viewed or clicked</li>
          <li>Search queries within our directory</li>
          <li>Traffic sources (where visitors come from)</li>
          <li>Device and browser information</li>
        </ul>

        <h3>Analytics Services We Use</h3>
        <p>
          We may use third-party analytics services to collect and analyze this data. These services use cookies to track user behavior across our website. The information collected is typically aggregated and anonymized.
        </p>

        <h3>Purpose</h3>
        <p>
          Analytics data helps us:
        </p>
        <ul>
          <li>Understand which AI tools are most popular</li>
          <li>Identify areas for improvement in our directory</li>
          <li>Optimize our content and navigation</li>
          <li>Make data-driven decisions about new features</li>
          <li>Improve overall user experience</li>
        </ul>
      </section>

      <section id="functionality-cookies">
        <h2>Functionality Cookies</h2>
        <p>
          Functionality cookies allow our website to remember choices you make and provide enhanced, personalized features.
        </p>
        
        <h3>What Functionality Cookies Remember</h3>
        <ul>
          <li>Your preferred language or region</li>
          <li>Search filters and category preferences</li>
          <li>Display settings and layout preferences</li>
          <li>Tools you've bookmarked or favorited</li>
        </ul>

        <h3>Benefits</h3>
        <p>
          These cookies enhance your experience by remembering your preferences so you don't have to reset them each time you visit. The information collected by functionality cookies is typically anonymized and cannot track your browsing activity on other websites.
        </p>
      </section>

      <section id="third-party-cookies">
        <h2>Third-Party Cookies</h2>
        <p>
          Some cookies on Toolnova are set by third-party services we use to enhance our website functionality and analyze user behavior.
        </p>
        
        <h3>Third-Party Services</h3>
        <p>
          We may use cookies from the following types of third-party services:
        </p>
        <ul>
          <li><strong>Analytics Providers:</strong> To track and analyze website usage</li>
          <li><strong>Content Delivery Networks (CDNs):</strong> To deliver website content efficiently</li>
          <li><strong>Social Media Platforms:</strong> To enable social sharing features</li>
          <li><strong>Affiliate Networks:</strong> To track referrals and commissions</li>
        </ul>

        <h3>Third-Party Privacy Policies</h3>
        <p>
          Third-party cookies are governed by the privacy policies of the respective third-party services. We recommend reviewing their privacy policies to understand how they collect and use your information:
        </p>
        <ul>
          <li>Google Analytics Privacy Policy</li>
          <li>Social media platform privacy policies</li>
          <li>Affiliate network privacy policies</li>
        </ul>
      </section>

      <section id="cookie-duration">
        <h2>Cookie Duration</h2>
        <p>
          Different cookies remain on your device for different lengths of time:
        </p>
        
        <h3>Session Cookies</h3>
        <p>
          Session cookies are temporary and are automatically deleted when you close your browser. We use session cookies to maintain your browsing session and remember your actions as you navigate between pages.
        </p>

        <h3>Persistent Cookies</h3>
        <p>
          Persistent cookies remain on your device for a set period, which can range from a few days to several years, depending on the cookie's purpose. Common durations include:
        </p>
        <ul>
          <li><strong>Short-term (days to weeks):</strong> Functionality cookies that remember recent preferences</li>
          <li><strong>Medium-term (months):</strong> Analytics cookies that track visitor patterns</li>
          <li><strong>Long-term (years):</strong> Cookies that remember long-term preferences or consent choices</li>
        </ul>
        <p>
          You can delete persistent cookies at any time through your browser settings.
        </p>
      </section>

      <section id="managing-cookies">
        <h2>Managing Cookies</h2>
        <p>
          You have several options for managing and controlling cookies on your device:
        </p>
        
        <h3>Browser Settings</h3>
        <p>
          Most web browsers allow you to control cookies through their settings. You can typically:
        </p>
        <ul>
          <li>View which cookies are stored on your device</li>
          <li>Delete all cookies or specific cookies</li>
          <li>Block all cookies from being set</li>
          <li>Block third-party cookies only</li>
          <li>Set your browser to notify you when a cookie is being set</li>
        </ul>

        <h3>How to Manage Cookies in Popular Browsers</h3>
        <ul>
          <li><strong>Google Chrome:</strong> Settings &gt; Privacy and security &gt; Cookies and other site data</li>
          <li><strong>Mozilla Firefox:</strong> Settings &gt; Privacy &amp; Security &gt; Cookies and Site Data</li>
          <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Manage Website Data</li>
          <li><strong>Microsoft Edge:</strong> Settings &gt; Cookies and site permissions &gt; Cookies and site data</li>
        </ul>

        <h3>Impact of Blocking Cookies</h3>
        <p>
          Please note that blocking or deleting cookies may impact your experience on Toolnova:
        </p>
        <ul>
          <li>Some features may not work properly</li>
          <li>Your preferences may not be saved</li>
          <li>You may need to re-enter information on each visit</li>
          <li>Certain pages may not display correctly</li>
        </ul>

        <h3>Opt-Out Options</h3>
        <p>
          For analytics cookies, you may be able to opt out through the following methods:
        </p>
        <ul>
          <li>Browser extensions that block analytics tracking</li>
          <li>Opt-out tools provided by analytics services</li>
          <li>Do Not Track (DNT) browser settings</li>
        </ul>
      </section>

      <section id="consent">
        <h2>Your Consent</h2>
        <p>
          When you first visit Toolnova, you may see a cookie consent banner informing you about our use of cookies. By continuing to use our website, you consent to our use of cookies as described in this Cookie Policy.
        </p>
        
        <h3>Withdrawing Consent</h3>
        <p>
          You can withdraw your consent at any time by:
        </p>
        <ul>
          <li>Adjusting your cookie preferences through our cookie settings (if available)</li>
          <li>Deleting cookies through your browser settings</li>
          <li>Blocking cookies through your browser settings</li>
        </ul>

        <h3>Essential Cookies</h3>
        <p>
          Please note that essential cookies do not require consent as they are necessary for the website to function. However, you can still block them through your browser settings if desired.
        </p>
      </section>

      <section id="changes">
        <h2>Changes to This Policy</h2>
        <p>
          We may update this Cookie Policy from time to time to reflect changes in our practices, technologies, or legal requirements. When we make changes, we will update the "Last Updated" date at the top of this policy.
        </p>
        <p>
          We encourage you to review this Cookie Policy periodically to stay informed about how we use cookies. Significant changes will be communicated through a notice on our website.
        </p>
      </section>

      <section id="contact">
        <h2>Contact Us</h2>
        <p>
          If you have any questions about our use of cookies or this Cookie Policy, please contact us:
        </p>
        <ul>
          <li><strong>Email:</strong> toolnova.hq@gmail.com</li>
          <li><strong>Contact Form:</strong> Available on our Contact page</li>
        </ul>
        <p>
          We are committed to transparency and will be happy to answer any questions you may have about our cookie practices.
        </p>
      </section>
    </LegalPageLayout>
  );
}